<template>
  <div class="pricing-cards">
    <b-row class="pricing-card">
      <b-col cols="12">
        <b-row class="justify-content-center">
          <b-col
            v-for="_package in packages"
            :key="_package.slug"
            class="pricing-card-item"
            :class="{'quote-card': _package.quote}"
            :lg="12 / (packages.length || 1)"
            md="6"
            sm="12"
          >
            <b-card
              align="center"
              class="position-relative h-100"
              body-class="d-flex flex-column justify-content-between"
            >
              <div
                v-if="!monthly && getDiscountPercentage(_package)"
                class="discount-percentage"
                dir="ltr"
              >
                {{ $t('subscription.save') }} {{ getDiscountPercentage(_package) }}%
              </div>
              <!-- img -->
              <div
                v-if="getPackageImage(_package)"
                class="package-image-container"
              >
                <b-img
                  :src="getPackageImage(_package)"
                  :class="`${_package.slug}-package-image`"
                  alt="svg img"
                />
              </div>
              <!--/ img -->
              <h3>{{ getTranslatedInfo(_package, 'title') }}</h3>

              <!-- annual plan -->
              <div class="annual-plan">
                <div class="plan-price mt-2 d-flex w-100 justify-content-center">
                  <template v-if="!_package.is_custom">
                    <div>
                      <sup class="font-medium-1 font-weight-bold text-red">$</sup>
                      <span class="pricing-basic-value font-weight-bolder text-red">{{ monthlyPlanShow ? _package.monthly_price : ((_package.yearly_price) / 12).toFixed(0) }}</span>
                    </div>
                    <div class="pricing-duration text-body font-medium-1 text-lowercase">
                      /{{ $tc('units.month', 1) }}
                      <br>
                      <span class="billed-annually-label">
                        <template v-if="monthlyPlanShow">
                          {{ _package.monthly_price ? $t('payment.billed-monthly') : $t('payment.free-forever') }}
                        </template>
                        <template v-else>
                          {{ (_package.yearly_price) ? $t('payment.billed-annually') : $t('payment.free-forever') }}
                        </template>
                      </span>
                    </div>
                  </template>
                  <template>
                    <div class="pricing-basic-value-custom font-weight-bolder text-red text-large">
                      {{ _package.price }}
                    </div>
                  </template>
                </div>

              </div>
              <!--/ annual plan -->

              <!-- plan benefit -->
              <b-list-group class="benefits-list list-group-circle text-left text-secondary flex-grow-1">
                <b-list-group-item
                  v-for="(feature,index) in getPackageBenefits(_package)"
                  :key="index"
                  :class="{'text-danger font-weight-bold': !_package.is_custom && feature.starred}"
                >
                  {{ _package.is_custom ? feature : feature.text }}
                </b-list-group-item>
              </b-list-group>
              <!--/ plan benefit -->

              <template v-if="superAdmin">
                <b-button
                  variant="outline-dark"
                  @click="$emit('select',_package)"
                >
                  {{ $t('forms.edit') }}
                </b-button>
              </template>
              <template v-else>
                <b-button
                  v-if="canPurchase(_package) || isPurchased(_package)"
                  v-ripple.400="'rgba(112, 95, 162, 0.15)'"
                  block
                  class="subscribe-btn"
                  :variant="isPurchased(_package) ? 'outline-success' : 'outline-secondary'"
                  :disabled="isPurchased(_package)"
                  @click="selectPackage(_package)"
                >
                  <template v-if="_package.is_custom">
                    {{ $t('subscription.get-quote') }}
                  </template>
                  <template v-else-if="!isPurchased(_package)">
                    {{ (_package.upgrade_price !== null && _package.upgrade_price >= 0 && !monthly && !isTrial) ? $t('subscription.upgrade') : $t('subscription.subscribe-now') }}
                  </template>
                  <template v-else>
                    {{ $t('subscription.your-current-plan') }}
                  </template>
                </b-button>

                <div
                  v-if="isPurchased(_package) && !currentPackageActions && _package.slug !== 'free'"
                  class="current-package-actions"
                >
                  <template v-if="!isRecurring">
                    <span
                      v-if="canRenew"
                      class="text-underline font-weight-semi-bold cursor-pointer"
                      @click="handleRenew"
                    >
                      {{ $t('subscription.renew') }}
                    </span>
                    <template v-if="canRenew">
                      {{ $t('general.or') }}
                    </template>
                  </template>
                  <span
                    class="text-underline font-weight-semi-bold cursor-pointer"
                    @click="cancelSubscription"
                  >
                    {{ $t('subscription.cancel-subscription') }}
                  </span>
                </div>
              </template>
            </b-card>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
    <div
      v-if="canGetExtraFree"
      class="subscribe-now-promo"
      v-html="$t('subscription.subscribe-get-two-months')"
    />

    <cancel-subscription-modal v-model="showCancelModal" />
    <quote-modal v-model="showQuoteModal" />
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BImg, BListGroup, BListGroupItem, BButton,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { mapGetters } from 'vuex'
import CancelSubscriptionModal from '@/views/admin/billing/components/CancelSubscriptionModal'
import PricingMixin from '@/mixins/pricing-mixin'
import QuoteModal from '@/views/admin/billing/components/QuoteModal'

const FREE_IMAGE = require('@/assets/images/illustrations/plans/free.svg')
const BASIC_IMAGE = require('@/assets/images/illustrations/plans/basic.svg')
const PRO_IMAGE = require('@/assets/images/illustrations/plans/pro.svg')

export default {
  name: 'PricingCards',
  inject: ['getTranslatedInfo'],
  components: {
    QuoteModal,
    CancelSubscriptionModal,
    BButton,
    BListGroup,
    BListGroupItem,
    BRow,
    BCol,
    BCard,
    BImg,
  },
  directives: {
    Ripple,
  },
  mixins: [PricingMixin],
  props: {
    showCustom: {
      type: Boolean,
      default: false,
    },
    hideTeaser: {
      type: Boolean,
      default: false,
    },
    monthly: {
      type: Boolean,
      default: false,
    },
    currentPackageActions: {
      type: Boolean,
      default: false,
    },
    forUpgrade: {
      type: Boolean,
      default: false,
    },
    superAdmin: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showCancelModal: false,
      showQuoteModal: false,
    }
  },
  computed: {
    ...mapGetters({
      regularPackages: 'pricing/regularPackages',
      isTrial: 'subscription/isTrial',
      isFree: 'subscription/isFree',
      isPaid: 'subscription/isPaid',
      purchasedPackage: 'subscription/currentPackage',
      canGetExtraFree: 'subscription/canGetExtraFree',
      isRecurring: 'subscription/isRecurringSubscriptionPayment',
      currentPackageMonthlyPrice: 'subscription/currentPackageMonthlyPrice',
      canRenew: 'subscription/canRenew',
      isExpired: 'subscription/isExpired',
    }),
    packages() {
      let packages = [...this.regularPackages]
      if (this.forUpgrade) {
        packages = packages.filter(_package => _package.monthly_price > this.currentPackageMonthlyPrice)
      }
      if (this.showCustom || !packages.length) {
        packages.push({
          is_custom: true,
          quote: true,
          locales: [{ locale: this.$i18n.locale, title: this.$t('subscription.custom') }],
          features: [this.$t('features.dedicated-account-manager'), this.$t('features.data-entry')],
          price: this.$t('subscription.get-quote'),
        })
      }
      return packages
    },
    monthlyPlanShow() {
      return this.monthly
    },
  },
  methods: {
    getPackageImage(_package) {
      if (_package.is_custom) return PRO_IMAGE

      switch (_package.slug) {
        case 'free': return FREE_IMAGE
        case 'basic': return BASIC_IMAGE
        case 'pro': return PRO_IMAGE
        default: return FREE_IMAGE
      }
    },
    getDiscountPercentage(_package) {
      if (this.isPurchased(_package)) {
        return 0
      }

      const monthlyPrice = _package.monthly_price ?? 0
      const yearlyPrice = (_package.yearly_price || 0) / 12
      const diff = monthlyPrice - yearlyPrice
      return Math.floor((diff * 100) / monthlyPrice)
    },
    isPurchased(_package) {
      return this.purchasedPackage?.id && this.purchasedPackage?.id === _package.id
    },
    canPurchase(_package) {
      return this.isTrial || _package.is_custom || this.purchasedPackage?.yearly_price < _package.yearly_price || this.isExpired || !this.canRenew
    },
    handleRenew() {
      this.$router.push({ name: 'billing:invoice', params: { id: 'renew' } })
    },
    cancelSubscription() {
      this.showCancelModal = true
    },
    selectPackage(_package) {
      if (_package.is_custom) {
        this.showQuoteModal = true
      } else {
        this.$emit('select', _package)
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-pricing.scss';
@import "@/assets/scss/utils/responsive.scss";

.pricing-cards {
  .pricing-card-item {
    max-width: 230px;

    @include respond-to-max('medium') {
      margin-bottom: 20px!important;
    }
  }

  .text-red {
    color: #FF055D!important;
  }

  .annual-plan {
    margin-bottom: 27px!important;
  }

  .pricing-basic-value-custom {
    font-size: 2.6rem!important;
  }

  .quote-card {
    .pricing-basic-value-custom {
      font-size: 28px!important;
    }
  }

  .pricing-duration {
    font-size: 12px!important;
    display: inline-block;
    margin-top: 20px;
  }

    .billed-annually-label {
    color: #FF055D!important;
    font-size: 8px!important;
    font-weight: 500!important;
  }

  .package-image-container {
    min-height: 130px;
    display: flex;
    justify-content: center;
    align-items: flex-end;
    margin-bottom: 18px;
  }

  .subscribe-now-promo {
    text-align: center;
    margin: 10px 43px;
    font-size: 20px;
    font-weight: 600;
    line-height: 1.75;
    color: var(--purple);
  }

  .benefits-list {
    .list-group-item {
      font-size: 11px;
      font-weight: 500;
      line-height: 1.73;
      &:after {
        width: 6.3px!important;
        height: 6.3px!important;
        background-size: 6px
      }

      [dir=ltr] & {
        padding-left: 14px;
      }

      [dir=rtl] & {
        padding-right: 14px;
      }
    }
  }

  .subscribe-btn {
    margin-top: 24px;
    font-size: 11px;
    font-weight: 500;
    line-height: 1.73;
    padding: 7px 5px 9px;
    width: 100%;
    text-align: center;
  }

  .discount-percentage {
    top: 15px;
    right: 17px;
    position: absolute;
    font-size: 10px;
    font-weight: 600;
    font-stretch: normal;
    font-style: normal;
    padding: 4px 8px;
    border-radius: 12px;
    color: #ffffff;
    background: #f8cd69;
  }

  .current-package-actions {
    font-size: 10px;
    color: var(--purple);
    margin-top: 5px;
  }

  .list-group.list-group-circle .list-group-item:after {
    background-size: 6px!important;
  }
}
</style>
